import * as RadixToast from '@radix-ui/react-toast';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';
import { CircleAlert, CircleCheck, Info, TriangleAlert, X, type LucideIcon } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { useDirection } from '@shared/hooks/useLocale';
import { Icon } from './Icon';

export type ToastTone = 'neutral' | 'success' | 'warning' | 'danger';

export interface ToastInput {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  action?: { label: string; onClick: () => void };
}

interface ToastItem extends ToastInput {
  id: number;
}

interface ToastState {
  items: ToastItem[];
  push: (toast: ToastInput) => void;
  dismiss: (id: number) => void;
}

let nextId = 1;

const useToasts = create<ToastState>((set) => ({
  items: [],
  // Max 3 stacked (design system §4.18).
  push: (toast) => set((state) => ({ items: [...state.items, { ...toast, id: nextId++ }].slice(-3) })),
  dismiss: (id) => set((state) => ({ items: state.items.filter((t) => t.id !== id) })),
}));

/** Fire-and-forget toast. Never for validation errors — those stay inline. */
export function toast(input: ToastInput): void {
  useToasts.getState().push(input);
}

const TONES: Record<ToastTone, { icon: LucideIcon; className: string }> = {
  neutral: { icon: Info, className: 'bg-surface-inverse text-on-inverse' },
  success: { icon: CircleCheck, className: 'bg-surface-raised text-primary border-s-4 border-success' },
  warning: { icon: TriangleAlert, className: 'bg-surface-raised text-primary border-s-4 border-warning' },
  danger: { icon: CircleAlert, className: 'bg-surface-raised text-primary border-s-4 border-danger' },
};

/** Viewport at the bottom end on desktop, top on phone (never over the bottom nav). */
export function Toaster({ children }: { children?: ReactNode }) {
  const { t } = useTranslation('common');
  const direction = useDirection();
  const items = useToasts((s) => s.items);
  const dismiss = useToasts((s) => s.dismiss);
  return (
    <RadixToast.Provider swipeDirection={direction === 'rtl' ? 'left' : 'right'} duration={6000} label={t('toast.region')}>
      {children}
      {items.map((item) => {
        const tone = TONES[item.tone ?? 'neutral'];
        return (
          <RadixToast.Root
            key={item.id}
            type={item.tone === 'danger' ? 'foreground' : 'background'}
            // Danger toasts persist until dismissed.
            duration={item.tone === 'danger' ? Number.POSITIVE_INFINITY : 6000}
            onOpenChange={(open) => {
              if (!open) dismiss(item.id);
            }}
            className={cn('flex w-full items-start gap-3 rounded-lg p-4 shadow-3', tone.className)}
          >
            <Icon icon={tone.icon} size={20} className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <RadixToast.Title className="text-sm font-semibold">{item.title}</RadixToast.Title>
              {item.description ? (
                <RadixToast.Description className="mt-0.5 text-sm opacity-90">{item.description}</RadixToast.Description>
              ) : null}
            </div>
            {item.action ? (
              <RadixToast.Action altText={item.action.label} asChild>
                <button type="button" className="link shrink-0 text-sm font-medium" onClick={item.action.onClick}>
                  {item.action.label}
                </button>
              </RadixToast.Action>
            ) : null}
            <RadixToast.Close aria-label={t('actions.dismiss')} className="shrink-0 rounded-sm p-1 opacity-80 hover:opacity-100">
              <Icon icon={X} size={16} />
            </RadixToast.Close>
          </RadixToast.Root>
        );
      })}
      <RadixToast.Viewport
        hotkey={['F8']}
        className={cn(
          'fixed z-toast flex w-full flex-col gap-2 p-4 outline-none',
          'inset-x-0 top-0 max-md:mx-auto max-md:max-w-md',
          'md:inset-x-auto md:bottom-0 md:end-0 md:top-auto md:w-[24rem]',
        )}
      />
    </RadixToast.Provider>
  );
}
